import _ from 'underscore';
import { useMemo } from 'react';
import { Message } from 'botframework-directlinejs';
import { useAdsCopilotContext } from '@bingads-webui/ads-copilot-ui';
import { v4 as uuidV4 } from 'uuid';

import {
  FluctuationResult, IPerformanceTimeSeries, matchingMetricTypes, MetricTypes, PerformanceTimeSeriesResult,
} from '../types';

export const getFluctuationsChartData = (performanceTimeSeriesResult: PerformanceTimeSeriesResult, fluctuationResult: FluctuationResult): IPerformanceTimeSeries[] | undefined => {
  const performanceTimeSeriesMap = _.isArray(performanceTimeSeriesResult?.Result) && performanceTimeSeriesResult.Result.reduce((acc: any, item) => {
    acc[item.metricName] = item;
    return acc;
  }, {});

  function createDataPoint(index: number, performanceTimeSeries: any, fluctuatedDateIndex: number) {
    const { dateTime, value } = performanceTimeSeries.metricValue[index] || {};
    return {
      DateTime: dateTime,
      Value: value,
      Fluctuate: index === fluctuatedDateIndex,
    };
  }

  const buildDates = (metricType: MetricTypes, startDate: any) => {
    const performanceTimeSeries = performanceTimeSeriesMap?.[(matchingMetricTypes as any)[metricType]];
    if (!performanceTimeSeries || Object.keys(performanceTimeSeries)?.length === 0 || !_.isArray(performanceTimeSeries.metricValue)) {
      return [];
    }

    const fluctuatedDateIndex = performanceTimeSeries?.metricValue?.findIndex((item: any) => item.dateTime?.substr(0, 10) === startDate) ?? -1;
    // buffer to ensure fluctuation date is the 6th date in the 7 date chart data array, for consistency and to show previous context
    const rangeStartBuffer = 5;
    const indexArrayLength = 7;

    // start index 5 below fluctuation date, but if below 0 prevent negative indexes
    let minIndex = Math.max(0, fluctuatedDateIndex - rangeStartBuffer);
    // end index at minIndex + 7 to ensure full data array, but prevent going past the available performanceTimeSeries data
    const maxIndex = Math.min(minIndex + indexArrayLength, performanceTimeSeries.metricValue.length);

    // if minInde to maxIndex isn't 7 items, decrease minIndex to add more items to the beginning of array to fill chart data
    if (maxIndex - minIndex < indexArrayLength) minIndex = Math.max(0, maxIndex - indexArrayLength);

    return _.range(minIndex, maxIndex).map((index) => createDataPoint(index, performanceTimeSeries, fluctuatedDateIndex));
  };

  if (_.isArray(fluctuationResult?.Result)) {
    return fluctuationResult.Result.map((item) => ({
      UniqueId: uuidV4(),
      MetricName: matchingMetricTypes[item.metricType] as MetricTypes,
      DataPoints: buildDates(item.metricType as MetricTypes, item.shiftStartDate),
    }));
  }
  return undefined;
};

export function useFluctuationsMessage(replyToId?: string) {
  const { activities } = useAdsCopilotContext() || {};

  const ret = useMemo(() => {
    let performanceTimeSeriesResult: PerformanceTimeSeriesResult | undefined;
    let fluctuationResult: FluctuationResult | undefined;
    let fluctuationSummaryMessage: Message | undefined;
    let isCompleted = false;

    if (activities && replyToId) {
      let i = activities.length - 1;

      // find all the fluctuations messages that where a reply to the given replyToId
      while (i >= 0 && (!performanceTimeSeriesResult || !fluctuationResult || !fluctuationSummaryMessage)) {
        const activity = activities[i];
        if ((activity as any).replyToId === replyToId) {
          if (activity.type === 'message' && (activity.value as any)?.PluginContext) {
            // found the natural language summary
            fluctuationSummaryMessage = activity;
          } else if (activity.type === 'event' && activity.name === 'pluginResults') {
            // find the fluctuations data
            fluctuationResult = _.find(activity.value, (pluginResult) => pluginResult.Name === 'AccountFluctuation' || pluginResult.Name === 'CampaignFluctuation');

            // find the chart data
            performanceTimeSeriesResult = _.find(activity.value, (pluginResult) => pluginResult.Name === 'PerformanceTimeSeries');
          }
        }

        i -= 1;
      }
    }

    let fluctuationsChartData: IPerformanceTimeSeries[] | undefined;
    if (performanceTimeSeriesResult && fluctuationResult) {
      fluctuationsChartData = getFluctuationsChartData(performanceTimeSeriesResult, fluctuationResult);
    }

    // performance time series and fluctuation data are expected to arrive at the same time
    if (performanceTimeSeriesResult || fluctuationResult) {
      // check if natural language summary has completed streaming
      isCompleted = (fluctuationSummaryMessage?.value as any)?.StreamingStatus === 'Completed';
    }

    return {
      isCompleted,
      fluctuationResult,
      fluctuationSummaryMessage,
      fluctuationsChartData,
      performanceTimeSeriesResult,
    };
  }, [activities, replyToId]);

  return ret;
}
